import React, {useMemo} from "react";
import {TourCatalog, TourOperator, TripItinerary} from "./contentfulModel.ts";
import {cdnImageWidth, compareMiceSubcategories, parseMiceSubtitle, textColorOn} from "./utils.tsx";

const INK = '#1D1B18';

export interface MiceIndexSelection {
    city?: string;
    type?: string;
    query?: string;
}

// Minúsculas y sin acentos, para que "saidia" encuentre "Saïdia" y "tanger" encuentre "Tánger"
const normalize = (text: string) => text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const searchableText = (pkg: TripItinerary) => normalize([
    pkg.name, pkg.subtitle, pkg.category, pkg.subcategory, pkg.itinerary?.[0]?.title,
].filter(Boolean).join(' '));

const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7"/>
        <path d="M20 20l-3.5-3.5"/>
    </svg>
);

const FOCUS_RING = 'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1D1B18]';

interface MiceCatalogIndexProps {
    tourCatalog: TourCatalog;
    tourOperator: TourOperator;
    goToSlide: (slideName: string) => void;
    selection: MiceIndexSelection;
    onSelectionChange: (selection: MiceIndexSelection) => void;
}

export const MiceCatalogIndex: React.FC<MiceCatalogIndexProps> = ({tourCatalog, tourOperator, goToSlide, selection, onSelectionChange}) => {
    const accent = tourOperator.primaryColor || '#E9CCCC';
    const onAccent = textColorOn(accent);
    const packages = tourCatalog.tourPackages ?? [];

    // Ciudades (= category) según tourPackagesCategoryOrder; las no listadas al final
    const cities = useMemo(() => {
        const packages = tourCatalog.tourPackages ?? [];
        const order: string[] = (tourCatalog as { tourPackagesCategoryOrder?: string[] }).tourPackagesCategoryOrder ?? [];
        const counts = new Map<string, number>();
        packages.forEach(p => {
            const city = p.category || 'Otros';
            counts.set(city, (counts.get(city) ?? 0) + 1);
        });
        const rank = (c: string) => (order.indexOf(c) === -1 ? order.length : order.indexOf(c));
        return Array.from(counts, ([name, count]) => ({name, count}))
            .sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
    }, [tourCatalog]);

    const activeCity = cities.some(c => c.name === selection.city) ? selection.city : cities[0]?.name;
    const cityPackages = packages.filter(p => (p.category || 'Otros') === activeCity);

    const typeCounts = new Map<string, number>();
    cityPackages.forEach(p => {
        if (p.subcategory) typeCounts.set(p.subcategory, (typeCounts.get(p.subcategory) ?? 0) + 1);
    });
    const types = Array.from(typeCounts, ([name, count]) => ({name, count}))
        .sort((a, b) => compareMiceSubcategories(a.name, b.name));

    // Al cambiar de ciudad el tipo seleccionado puede no existir: se cae al primero disponible
    const activeType = types.some(t => t.name === selection.type) ? selection.type : types[0]?.name;

    const visiblePackages = cityPackages
        .filter(p => !activeType || p.subcategory === activeType)
        .sort((a, b) => a.name.localeCompare(b.name));

    const selectCity = (city: string) => onSelectionChange({city});

    // Búsqueda en todas las ciudades y tipos; cada palabra tiene que aparecer en el espacio
    const query = selection.query ?? '';
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    const isSearching = terms.length > 0;
    const cityRank = (city?: string) => cities.findIndex(c => c.name === (city || 'Otros'));
    const searchResults = useMemo(() => {
        if (!isSearching) return [];
        return packages
            .filter(p => {
                const text = searchableText(p);
                return terms.every(t => text.includes(t));
            })
            .sort((a, b) => cityRank(a.category) - cityRank(b.category)
                || compareMiceSubcategories(a.subcategory ?? '', b.subcategory ?? '')
                || a.name.localeCompare(b.name));
    }, [query, tourCatalog]); // eslint-disable-line react-hooks/exhaustive-deps

    const setQuery = (value: string) => onSelectionChange({...selection, query: value});

    const renderCard = (pkg: TripItinerary) => {
        const {stars, rest} = parseMiceSubtitle(pkg.subtitle);
        const image = cdnImageWidth(pkg.introductionImage?.file || pkg.mainImage?.file, 640);
        const slideName = pkg.category + "-" + pkg.name + (pkg.subtitle ? "-" + pkg.subtitle : "");
        // En resultados de búsqueda se mezclan ciudades y tipos, así que se indican en la tarjeta
        const context = isSearching ? [pkg.category, pkg.subcategory].filter(Boolean).join(' · ') : null;
        return (
            <button key={slideName} type="button" onClick={() => goToSlide(slideName)}
                    className={`flex flex-col text-left rounded-2xl overflow-hidden bg-white cursor-pointer border-0 p-0 shadow-[0_1px_2px_rgba(29,27,24,0.06),0_8px_24px_rgba(29,27,24,0.06)] hover:shadow-[0_1px_2px_rgba(29,27,24,0.08),0_12px_32px_rgba(29,27,24,0.12)] transition-shadow ${FOCUS_RING}`}>
                <div className="relative w-full h-[180px] md:h-[196px] bg-[#EDE7DC]">
                    {image && (
                        <img crossOrigin="anonymous" src={image} alt={pkg.name} loading="lazy"
                             className="w-full h-full object-cover"/>
                    )}
                    {stars && (
                        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 text-xs font-semibold">
                            {stars}
                        </span>
                    )}
                </div>
                <div className="px-[18px] pt-4 pb-[18px] flex flex-col gap-1.5">
                    {context && (
                        <div className="text-[11px] font-semibold tracking-wider uppercase text-[#6B645B]">{context}</div>
                    )}
                    <div className="text-base font-semibold leading-snug">{pkg.name}</div>
                    {rest.length > 0 && (
                        <div className="text-[13px] text-[#6B645B] line-clamp-2">{rest.join(' · ')}</div>
                    )}
                </div>
            </button>
        );
    };

    return (
        <div className="flex flex-col md:flex-row w-full h-full overflow-y-auto overscroll-contain md:overflow-hidden bg-white text-left" style={{color: INK}}>
            <aside className="md:w-[400px] md:flex-shrink-0 bg-[#F7F6F2] px-5 pt-6 pb-4 md:px-12 md:pt-14 md:pb-10 flex flex-col gap-6 md:gap-8 md:overflow-y-auto">
                <div className="flex flex-col gap-3">
                    <div className="text-xs font-semibold tracking-[0.14em] uppercase text-[#6B645B]">
                        Catálogo {tourCatalog.title || tourCatalog.name}
                    </div>
                    {tourCatalog.subtitle && (
                        <h1 className="m-0 text-2xl md:text-[30px] leading-tight font-bold" style={{fontFamily: 'var(--heading-font)'}}>
                            {tourCatalog.subtitle}
                        </h1>
                    )}
                    <p className="m-0 text-sm leading-relaxed text-[#6B645B]">
                        {packages.length} espacios en {cities.length} destinos.
                    </p>
                </div>

                <div role="search" className="relative">
                    <label htmlFor="mice-search" className="sr-only">Buscar espacio</label>
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B645B] pointer-events-none"><SearchIcon/></span>
                    <input id="mice-search" type="search" value={query} autoComplete="off"
                           placeholder="Buscar hotel, restaurante, zona…"
                           onChange={e => setQuery(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Escape') setQuery(''); }}
                           className="w-full min-h-[48px] pl-11 pr-4 rounded-xl bg-white border border-[#E4DED4] text-[15px] text-[#1D1B18] placeholder:text-[#6B645B] focus:outline-none focus:border-[#1D1B18]"/>
                </div>

                <nav aria-label="Ciudades" className="swiper-no-swiping flex md:flex-col gap-1.5 md:gap-0.5 overflow-x-auto hide-scrollbar -mx-5 px-5 md:mx-0 md:px-0">
                    {cities.map(city => {
                        const active = !isSearching && city.name === activeCity;
                        return (
                            <button key={city.name} type="button" onClick={() => selectCity(city.name)}
                                    aria-current={active ? 'true' : undefined}
                                    className={`flex-shrink-0 flex items-center justify-between gap-3 min-h-[44px] px-4 rounded-[10px] text-sm md:text-base cursor-pointer border-0 ${active ? 'bg-white font-semibold' : 'bg-white/60 md:bg-transparent hover:bg-white/70'} ${FOCUS_RING}`}>
                                <span>{city.name}</span>
                                <span className="text-[13px] font-normal text-[#6B645B]">{city.count}</span>
                            </button>
                        );
                    })}
                </nav>

                {!!tourCatalog.partnerLogo?.file && (
                    <img src={tourCatalog.partnerLogo.file} alt="Partner" className="hidden md:block w-36 mt-auto"/>
                )}
            </aside>

            <main className="flex-1 min-w-0 px-5 pt-4 pb-10 md:px-14 md:pt-14 md:pb-10 flex flex-col gap-5 md:gap-7 md:overflow-y-auto">
                {isSearching ? (
                    <>
                    <header>
                        <div className="text-[13px] text-[#6B645B]" aria-live="polite">
                            {searchResults.length === 1 ? '1 resultado' : `${searchResults.length} resultados`}
                        </div>
                        <h2 className="m-0 mt-1 text-3xl md:text-[40px] font-bold leading-none break-words" style={{fontFamily: 'var(--heading-font)'}}>
                            “{query.trim()}”
                        </h2>
                    </header>
                    {searchResults.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                            {searchResults.map(renderCard)}
                        </div>
                    ) : (
                        <div className="flex flex-col items-start gap-4 py-6">
                            <p className="m-0 text-[15px] text-[#4A443C]">No hay espacios que coincidan con la búsqueda.</p>
                            <button type="button" onClick={() => setQuery('')}
                                    className={`min-h-[44px] px-5 rounded-xl bg-[#F7F6F2] text-sm font-semibold cursor-pointer border-0 ${FOCUS_RING}`}>
                                Borrar búsqueda
                            </button>
                        </div>
                    )}
                    </>
                ) : (
                    <>
                    <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6">
                        <div>
                            <div className="text-[13px] text-[#6B645B]">{cityPackages.length} espacios</div>
                            <h2 className="m-0 mt-1 text-3xl md:text-[40px] font-bold leading-none" style={{fontFamily: 'var(--heading-font)'}}>
                                {activeCity}
                            </h2>
                        </div>
                        {types.length > 0 && (
                            <div role="tablist" aria-label="Tipo de espacio" className="swiper-no-swiping flex gap-2 p-1.5 bg-[#F7F6F2] rounded-full overflow-x-auto hide-scrollbar self-start md:self-auto max-w-full">
                                {types.map(type => {
                                    const active = type.name === activeType;
                                    return (
                                        <button key={type.name} type="button" role="tab" aria-selected={active}
                                                onClick={() => onSelectionChange({city: activeCity, type: type.name})}
                                                className={`flex-shrink-0 min-h-[44px] px-4 md:px-5 rounded-full text-sm cursor-pointer border-0 ${active ? 'font-semibold' : 'bg-transparent text-[#4A443C] font-medium'} ${FOCUS_RING}`}
                                                style={active ? {backgroundColor: accent, color: onAccent} : undefined}>
                                            {type.name} · {type.count}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </header>

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                        {visiblePackages.map(renderCard)}
                    </div>
                    </>
                )}
            </main>
        </div>
    );
};
